import type { PrecompiledSet } from "../types/Entities";

/**
 * PROG-ENGINE-V2/5 (#d55fc4ee) — divergence-vs-suggested advisory helper.
 *
 * Compares a completed downstream set's executed `workLoad` / `workReps`
 * against the engine's projected (suggested) values. Used by the advisory
 * pill in the per-set header to surface "Esecuzione differisce dal
 * suggerito" feedback non-intrusively after the fact.
 *
 * Tolerances per architect plan #72a6c050 §2 Strategy B + Q1 (user
 * approved):
 * - load: relative difference > 5% → flag direction (above / below).
 * - reps: exact mismatch → flag direction. A range-string `workReps`
 *   ("8-12") is in-tolerance when the executed integer is inside the band
 *   (inclusive on both bounds); a range-string `suggestedReps` is treated
 *   the same way against the numeric executed value. Two numbers compare
 *   directly. A range vs. range comparison flags only when the executed
 *   midpoint falls outside the suggested band.
 *
 * Returns null when there is nothing to compare (no executed value, no
 * suggested value, or unparseable inputs). Returning `{}` would force
 * callers to do an explicit emptiness check, so the API contract is "non-
 * null result means at least one axis diverged".
 */

const LOAD_TOLERANCE = 0.05; // 5% relative.

export interface SetDivergence {
  load?: "above" | "below";
  reps?: "above" | "below";
}

/** Parse a reps value (number or `"8"` / `"8-12"` string) into a `[lo, hi]`
 * band. Returns null when unparseable. A single integer surfaces as
 * `[n, n]` so range/single comparisons share a code path. */
function parseRepsBand(
  v: number | string | null | undefined,
): [number, number] | null {
  if (v == null) return null;
  if (typeof v === "number") {
    if (!Number.isFinite(v) || v <= 0) return null;
    return [v, v];
  }
  const trimmed = v.trim();
  if (trimmed === "") return null;
  const m = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (m) {
    const lo = parseInt(m[1], 10);
    const hi = parseInt(m[2], 10);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo <= 0 || hi <= 0) {
      return null;
    }
    return lo <= hi ? [lo, hi] : [hi, lo];
  }
  const n = parseInt(trimmed, 10);
  if (Number.isFinite(n) && n > 0 && String(n) === trimmed) return [n, n];
  return null;
}

/** Centre of a `[lo, hi]` band, rounded to 1 decimal for the comparison. */
function bandCentre(b: [number, number]): number {
  return Math.round(((b[0] + b[1]) / 2) * 10) / 10;
}

export function computeSetDivergence(
  set: PrecompiledSet | null | undefined,
  suggestedLoad: number | null | undefined,
  suggestedReps: number | string | null | undefined,
): SetDivergence | null {
  if (!set) return null;
  const result: SetDivergence = {};

  // Load axis: compare numeric workLoad to numeric suggestedLoad.
  const executedLoad =
    set.workLoad != null && Number.isFinite(Number(set.workLoad))
      ? Number(set.workLoad)
      : null;
  const sugLoad =
    suggestedLoad != null && Number.isFinite(suggestedLoad)
      ? suggestedLoad
      : null;
  if (executedLoad != null && sugLoad != null && sugLoad > 0) {
    const rel = (executedLoad - sugLoad) / sugLoad;
    if (rel > LOAD_TOLERANCE) result.load = "above";
    else if (rel < -LOAD_TOLERANCE) result.load = "below";
  }

  // Reps axis: parse both sides as bands; flag when the executed centre
  // falls outside the suggested band (or the bands don't overlap at all).
  const execBand = parseRepsBand(set.workReps);
  const sugBand = parseRepsBand(suggestedReps);
  if (execBand && sugBand) {
    const execC = bandCentre(execBand);
    if (execC > sugBand[1]) result.reps = "above";
    else if (execC < sugBand[0]) result.reps = "below";
  }

  if (result.load == null && result.reps == null) return null;
  return result;
}
